import { SearchResult } from '@/models/Repository'
import { MenuTypes } from '@/models/Simulation/Constants'
import MenuApi from '@/api/Menu'
import { CreativeTypes } from '@/models/Creative'
import IOApi from '@/api/IOApi'
import AdvertiserApi from '@/api/AdvertiserApi'
import moment from 'moment/moment'
import 'moment/locale/ja'
import dayjs from 'dayjs'

const assignModel = (data, enable) => {
  return data.map(o => new MenuModel(o, enable))
}
const assignRequestRecordsModel = data => {
  if (!data) return undefined
  return data.map(o => new RequestRecordModel(o))
}
export class MenuModel {
  constructor(item, enable) {
    const {
      id,
      menuType,
      ownerProfileId,
      resourceType,
      resourceId,
      resourceName,
      name,
      deliveryTargets,
      description,
      unitType,
      grossPrice,
      netPrice,
      currency,
      timeZone,
      costPlusMarginPercentage,
      startDate,
      endDate,
      status,
      latestRequest,
      requests,
      creativeType,
      supportedAgencyMargins,
    } = item
    this.id = Number(id) || undefined
    this.menuType = menuType || undefined
    this.ownerProfileId = Number(ownerProfileId) || undefined
    this.resourceType = resourceType || undefined
    this.resourceId = Number(resourceId) || undefined
    this.resourceName = resourceName || undefined
    this.name = name || undefined
    this.deliveryTargets = deliveryTargets || []
    this.description = description || undefined
    this.unitType = unitType || undefined
    this.netPrice = netPrice || undefined
    this.costPlusMarginPercentage = costPlusMarginPercentage || undefined
    this.grossPrice = grossPrice || undefined
    this.currency = currency || undefined
    this.timeZone = timeZone || undefined
    this.endDate = endDate || undefined
    this.startDate = startDate || undefined
    this.status = status || undefined
    this.latestRequest = new RequestRecordModel(latestRequest) || {}
    this.requests = assignRequestRecordsModel(requests) || []
    this.creativeType = creativeType || undefined
    this.supportedAgencyMargins = supportedAgencyMargins || []
    if (typeof enable === 'boolean') {
      this.enable = enable
    } else {
      this.enable = undefined
    }
  }
  get isNew() {
    return !this.id
  }
  get hasLatestRequest() {
    return this.latestRequest.id != null
  }
  get canChangeRequest() {
    return this.id != null && this.latestRequest.requestStatus !== 'waiting'
  }
  get unitTypeUpperCase() {
    return this.unitType ? this.unitType.toUpperCase() : ''
  }
  get unitTypeString() {
    let result = ''
    switch (this.unitType) {
      case 'cpc':
        result = 'CPC（1クリック単価）'
        break
      case 'cpm':
        result = 'CPM（1000回表示あたり単価）'
        break
      case 'cost_plus_margin':
        result = 'オークションCPM（配信状況によって表示単価が変動）'
        break
    }
    return result
  }
  get dateRange() {
    if (this.startDate == null && this.endDate == null) {
      return ''
    }
    const from = this.startDate || ' '
    const to = this.endDate || ' '
    return from + ' 〜 ' + to
  }
  get showGreyRecord() {
    return this.id == null
  }
  get resourceTypeString() {
    let result = ''
    switch (this.resourceType) {
      case 'advertiser':
        result = '広告主'
        break
      case 'oem':
        result = '共通'
        break
      case 'agency':
        result = '代理店'
        break
      case 'insertion_order':
        result = '案件'
        break
    }
    return result
  }
  get useDateRange() {
    if (!this.startDate || !this.endDate) return false
    return true
  }
  get menuTypeString() {
    let result = ''
    switch (this.menuType) {
      case 'geo':
        result = 'ジオターゲティング'
        break
      default:
        result = 'ジオターゲティング'
      // 他はまだ公開しないのでこれだけ
    }
    return result
  }
  get isStatusActive() {
    return this.status === 'active'
  }
  get requestType() {
    if (this.id == null) return 'new'
    return this.requests.length > 1 ? 'update' : 'new'
  }
  get isRequestPassed() {
    return this.id != null
  }
  get displayData() {
    return this.isRequestPassed ? this : this.latestRequest
  }
  get scopeString() {
    let result = ''
    switch (this.resourceType) {
      case 'system_owner':
        result = 'ネットワーク ' + this.resourceName + ' 全案件'
        break
      case 'agency':
        result = '代理店 ' + this.resourceName + ' 全案件'
        break
      case 'advertiser':
        result = '広告主 ' + this.resourceName + ' 全案件'
        break
      case 'insertion order':
        result = '案件 ' + this.resourceName
        break
    }
    return result
  }
  get isInDateRange() {
    if (this.startDate != null) {
      if (dayjs().isBefore(this.startDate)) {
        return false
      }
    }
    if (this.endDate != null) {
      if (dayjs().isAfter(this.endDate)) {
        return false
      }
    }
    return true
  }
  get resourceRangeText() {
    // advertiser用の「使用可能範囲」テキスト
    if (this.resourceType === 'insertion_order') {
      return this.resourceName
    }
    return '全案件'
  }
  static isIncludeBanner(menu) {
    return menu.creativeType === CreativeTypes.BANNER
  }
  static isGeoTargeting(menu) {
    return menu.menuType === MenuTypes.Geo
  }
  static async search({ keyword, id, pageSize, pageNo } = {}) {
    const query = { keyword, id, isIncludeDeleted: false }
    const result = await MenuApi.search(query, pageSize, pageNo)
    const items = assignModel(result.items)
    return new SearchResult({
      count: result.count,
      pageNo: result.pageNo,
      pageSize,
      items,
    })
  }
  static async searchForAdvertiser(advertiserId, pageSize) {
    const startDate = dayjs().format('YYYY-MM-DDTHH:mm:ss') + '+09:00'
    const result = await MenuApi.searchForAdvertiser(
      advertiserId,
      startDate,
      pageSize,
    )
    const items = assignModel(result.items)
    return new SearchResult({
      count: result.count,
      pageNo: result.pageNo,
      items,
    })
  }

  // adv belong userが叩く場合
  static async searchForSimulationPlanFromAdvBelongUser(advertiserId) {
    const result = await MenuApi.searchForSimulationPlan(advertiserId)
    const items = assignModel(result.items)
    return new SearchResult({
      items,
    })
  }

  // adv以上のuserが叩く場合
  static async searchForSimulationPlanByAdvertiserId(
    advertiserId,
    agencyMargin,
  ) {
    const result = await MenuApi.searchForSimulationPlan(
      advertiserId,
      agencyMargin,
    )
    const items = assignModel(result.items)
    return new SearchResult({
      items,
    })
  }

  // adv以上のuserが叩く場合
  static async searchForSimulationPlanByAgencyId(agencyId, agencyMargin) {
    const result = await MenuApi.searchForSimulationPlan(
      undefined,
      agencyMargin,
      agencyId,
    )
    const items = assignModel(result.items)
    return new SearchResult({
      items,
    })
  }

  async edit({ name, description, status }) {
    try {
      const params = { name, description, status }
      await MenuApi.editMenu(params, this.id)
      this.name = name
      this.description = description
      this.status = status
      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }
  async requestEdit({ start, end }) {
    try {
      const params = {
        ownerProfileId: this.ownerProfileId,
        menuId: this.id,
        name: this.name,
        menuType: this.menuType,
        description: this.description,
        unitType: this.unitType,
        currency: this.currency,
        netPrice: this.netPrice,
        grossPrice: this.grossPrice,
        startDate: start,
        endDate: end,
      }
      await MenuApi.requestEdit(params)
    } catch (e) {
      console.error(e)
      return false
    }
  }
}

export class RequestRecordModel {
  constructor(item = {}) {
    const {
      id,
      menuType,
      ownerProfileId,
      resourceType,
      resourceId,
      resourceName,
      name,
      description,
      unitType,
      netPrice,
      grossPrice,
      currency,
      timeZone,
      startDate,
      endDate,
      requestStatus,
      reason,
      requestTime,
      requestAccountId,
      requestAccountName,
    } = item
    this.id = Number(id) || undefined
    this.menuType = menuType || undefined
    this.menuType = menuType || undefined
    this.menuType = menuType || undefined
    this.ownerProfileId = Number(ownerProfileId) || undefined
    this.resourceType = resourceType || undefined
    this.resourceId = Number(resourceId) || undefined
    this.resourceName = resourceName || undefined
    this.name = name || undefined
    this.description = description || undefined
    this.unitType = unitType || undefined
    this.netPrice = netPrice || undefined
    this.grossPrice = grossPrice || undefined
    this.currency = currency || undefined
    this.timeZone = timeZone || undefined
    this.endDate = endDate || undefined
    this.startDate = startDate || undefined
    this.requestStatus = requestStatus || undefined
    this.reason = reason || undefined
    this.requestTime = requestTime || undefined
    this.requestAccountId = requestAccountId || undefined
    this.requestAccountName = requestAccountName || undefined
  }
  get dateRange() {
    if (!this.startDate || !this.endDate) return '使用期限なし'
    return this.startDate + ' - ' + this.endDate
  }
  get unitTypeUpperCase() {
    return this.unitType ? this.unitType.toUpperCase() : ''
  }
  get unitTypeString() {
    let result = ''
    switch (this.unitType) {
      case 'cpc':
        result = 'CPC（1クリック単価）'
        break
      case 'cpm':
        result = 'CPM（1000回表示あたり単価）'
        break
      case 'cost_plus_margin':
        result = '・オークションCPM（配信状況によって表示単価が変動）'
        break
    }
    return result
  }
  get netPriceString() {
    return typeof this.netPrice === 'number' ? '¥ ' + this.netPrice : ''
  }
  get statusDisplayName() {
    switch (this.requestStatus) {
      case 'waiting':
        return '承認待ち'
      case 'approved':
        return '承認済'
      case 'rejected':
        return '却下'
      case 'cancel':
        return 'キャンセル'
      default:
        return ''
    }
  }
  get resourceTypeString() {
    let result = ''
    switch (this.resourceType) {
      case 'advertiser':
        result = '広告主'
        break
      case 'oem':
        result = '共通'
        break
      case 'agency':
        result = '代理店'
        break
      case 'insertion_order':
        result = '案件'
        break
    }
    return result
  }
  get useDateRange() {
    if (!this.startDate || !this.endDate) return false
    return true
  }
  get menuTypeString() {
    let result = ''
    switch (this.menuType) {
      case 'geo':
        result = 'ジオターゲティング'
        break
      default:
        result = 'ジオターゲティング'
      // 他はまだ公開しないのでこれだけ
    }
    return result
  }
  get isCancelable() {
    return this.requestStatus === 'waiting'
  }
  get isRejected() {
    return this.requestStatus === 'rejected'
  }
  get requestTimeToMinites() {
    if (!this.requestTime) return ''
    moment.locale('ja')
    let obj = moment(this.requestTime)
    return moment(obj).format('YYYY-MM-DD HH:mm')
  }
  get scopeString() {
    let result = ''
    switch (this.resourceType) {
      case 'system_owner':
        result = 'ネットワーク ' + this.resourceName + ' 全案件'
        break
      case 'agency':
        result = '代理店 ' + this.resourceName + ' 全案件'
        break
      case 'advertiser':
        result = '広告主 ' + this.resourceName + ' 全案件'
        break
      case 'insertion order':
        result = '案件 ' + this.resourceName
        break
    }
    return result
  }
  static async deleteRequest(id) {
    let ret = await MenuApi.deleteRequest(id)
    return ret
  }
  static async editAgencyMargin(id) {
    let ret = await MenuApi.editMenu(id)
    return ret
  }
  async requestCancel() {
    try {
      await MenuApi.requestCancel(this.id)
      this.requestStatus = 'cancel'
    } catch (e) {
      console.error(e)
      return false
    }
  }
}

export class CreateForm {
  constructor(item = {}) {
    const {
      systemOwner,
      scope,
      advertiser,
      insertionOrder,
      menuType,
      name,
      description,
      unitType,
      currency,
      netPrice,
      grossPrice,
      startDate,
      endDate,
    } = item
    // systemOwner は ownerProfile の取得に必要な場合がある
    this.systemOwner = systemOwner
    this.scope = scope || undefined
    this.advertiser = advertiser || undefined
    this.insertionOrder = insertionOrder || undefined
    this.menuType = menuType || undefined
    this.name = name || undefined
    this.description = description || undefined
    this.unitType = unitType || undefined
    this.currency = currency
    this.netPrice = netPrice || undefined
    this.grossPrice = grossPrice || undefined
    this.startDate = startDate || undefined
    this.endDate = endDate || undefined
    // 画面から設定しないけどAPI的に必要
    this.currency = 'JPY'
  }
  setStartDate(dateStr) {
    if (!dateStr) return
    moment.locale('ja')
    let obj = moment(dateStr)
    this.startTime = moment(obj).format('YYYY-MM-DD')
  }
  setEndDate(dateStr) {
    if (!dateStr) return
    moment.locale('ja')
    let obj = moment(dateStr)
    this.endTime = moment(obj).format('YYYY-MM-DD')
  }
  get useDateRange() {
    if (!this.startDate || !this.endDate) return false
    return true
  }
  /**
   * ownerProfileId は下記のルールで決定される。
   * - 案件がある場合： insertionOrder.ownerProfileId
   * - 広告主がある場合： advertiser.ownerProfileId
   * - 上記該当しない場合： systemOwner.ownerProfileId
   */
  get ownerProfileId() {
    if (this.insertionOrder) return this.insertionOrder.ownerProfileId
    if (this.advertiser) return this.advertiser.ownerProfileId
    return this.systemOwner.ownerProfileId
  }
  static async createMenu(params) {
    let ret = await MenuApi.createMenu(params)
    return ret
  }
  static async searchAdvertiser({ keyword, minPermissionLevel }) {
    let ret = await AdvertiserApi.searchAdvertiser({
      keyword,
      minPermissionLevel,
    })
    return ret
  }
  static async searchInsertionOrder({ keyword, id }) {
    let ret = await IOApi.fetchIOList({ advertiserId: id, keyword })
    return ret
  }
  async execCreate() {
    try {
      const params = {
        ownerProfileId: this.ownerProfileId,
        menuId: undefined,
        name: this.name,
        menuType: this.menuType,
        description: this.description,
        unitType: this.unitType,
        currency: this.currency,
        netPrice:
          this.unitType === 'cost_plus_margin' ? undefined : this.netPrice,
        grossPrice: this.grossPrice,
        startDate: this.startDate,
        endDate: this.endDate,
      }
      console.log(params)
      await MenuApi.requestEdit(params)
    } catch (e) {
      console.error(e)
      throw new Error('申請に失敗しました')
    }
  }
}
